"use client";

import { useClientConfig } from "@providers/app-provider/client-app-provider";
import { StorageBucket, StorageFile } from "@sisnaker-react-core/karirhub-model-type";
import { Button, cn } from "@ubud-ui/react-ui-kit";
import { DocumentIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useRef, useState } from "react";
import { useStorageUpload } from "../hooks/use-storage-upload";

type UploadedFile = StorageFile;

type StorageFileUploaderProps = {
  bucket: StorageBucket;
  maxFiles?: number;
  accept?: string;
  maxSize?: number;
  initialFiles?: UploadedFile[];
  onChange?: (data: { uploadedFiles: UploadedFile[]; files: File[] }) => void;
  label?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
};

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function getFileExtension(fileName: string): string {
  return fileName.slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase();
}

function isImageFile(fileName: string, fileType?: string): boolean {
  if (fileType?.startsWith("image/")) return true;
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];
  return imageExtensions.includes(getFileExtension(fileName));
}

function isAcceptedFile(file: File, accept?: string): boolean {
  if (!accept) return true;
  const patterns = accept
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (patterns.length === 0) return true;

  const extension = getFileExtension(file.name);

  for (const pattern of patterns) {
    if (pattern === "*" || pattern === "*/*") return true;
    if (pattern.endsWith("/*")) {
      const prefix = pattern.replace("/*", "/");
      if (file.type.startsWith(prefix)) return true;
    }
    if (pattern.startsWith(".")) {
      if (extension === pattern.replace(".", "").toLowerCase()) return true;
    }
    if (file.type === pattern) return true;
  }

  return false;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function fetchFileDetails(apiUrl: string, token: string, storageCode: StorageBucket, fileId: string): Promise<UploadedFile | null> {
  const response = await fetch(`${apiUrl}/storage/v1/storages/${storageCode}/files/${fileId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error("Gagal mengambil detail file.");
  }

  const result = await response.json();
  const data = result?.data;

  if (!data) return null;

  return {
    id: data.id,
    name: data.name,
    download_link: data.download_link || "",
    bucket: data.bucket || { id: "", name: "", code: "" },
    bucket_id: data.bucket_id || "",
    user: null,
    user_id: data.user_id || "",
    created_at: data.created_at,
    updated_at: data.updated_at,
  } as UploadedFile;
}

function getDownloadUrl(apiUrl: string | undefined, bucket: StorageBucket, fileId: string): string | null {
  if (!apiUrl) return null;
  return `${apiUrl}/storage/v1/storages/${bucket}/files/${fileId}/download`;
}

function buildAcceptLabel(accept?: string): string {
  if (!accept) return "Semua format";

  const mimeLabels: Record<string, string> = {
    "image/jpeg": "JPG",
    "image/jpg": "JPG",
    "image/png": "PNG",
    "image/gif": "GIF",
    "image/webp": "WEBP",
    "image/svg+xml": "SVG",
    "application/pdf": "PDF",
    "application/msword": "DOC",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  };

  const extensionLabels: Record<string, string> = {
    jpg: "JPG",
    jpeg: "JPG",
    png: "PNG",
    gif: "GIF",
    webp: "WEBP",
    svg: "SVG",
    pdf: "PDF",
    doc: "DOC",
    docx: "DOCX",
  };

  const labels = accept
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      if (part.startsWith(".")) {
        const ext = part.replace(".", "").toLowerCase();
        return extensionLabels[ext] || ext.toUpperCase();
      }
      return mimeLabels[part] || part.toUpperCase();
    });

  return Array.from(new Set(labels)).join(", ");
}

export function StorageFileUploader({
  bucket,
  maxFiles = 1,
  accept,
  maxSize,
  initialFiles = [],
  onChange,
  label,
  helperText,
  required,
  disabled,
}: StorageFileUploaderProps) {
  const config = useClientConfig();
  const apiUrl = config?.config.apiUrl;
  const token = config?.accessToken;
  const { upload, isUploading, progress, error: uploadError } = useStorageUpload();

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(initialFiles);
  const [fileSizes, setFileSizes] = useState<Record<string, number>>({});
  const [isDragging, setIsDragging] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const remainingSlots = maxFiles === 0 ? Number.MAX_SAFE_INTEGER : Math.max(0, maxFiles - uploadedFiles.length);
  const canUploadMore = remainingSlots > 0;

  // Sync the externally provided initialFiles without triggering a
  // "Maximum update depth exceeded" loop. The update is idempotent: when the
  // incoming files describe the same set we already hold, we keep the current
  // state (only enriching entries whose metadata arrived later, e.g. a
  // placeholder being replaced by the real fetched file). This lets async
  // initial files populate the uploader while preventing the effect from
  // re-firing on every parent render.
  //
  // The effect is keyed on a content signature instead of the array reference
  // because consumers may pass inline arrays or rely on the `[]` default
  // parameter, both of which produce a new reference on every render. Keying
  // on the reference would re-run this sync constantly and wipe files that
  // were just uploaded.
  const initialFilesSignature = (initialFiles || []).map((file) => `${file.id}::${file.download_link}`).join("|");

  useEffect(() => {
    setUploadedFiles((prev) => {
      const incoming = initialFiles || [];
      const prevIds = prev.map((file) => file.id).join("|");
      const incomingIds = incoming.map((file) => file.id).join("|");

      if (prevIds !== incomingIds) {
        return incoming;
      }

      let changed = false;
      const merged = prev.map((current) => {
        const match = incoming.find((file) => file.id === current.id);
        if (match && !current.download_link && match.download_link) {
          changed = true;
          return match;
        }
        return current;
      });

      return changed ? merged : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilesSignature]);

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      if (!apiUrl || !token) {
        setLastError("Konfigurasi upload tidak tersedia.");
        return;
      }

      setLastError(null);

      const fileArray = Array.from(files);
      const filesToProcess = fileArray.slice(0, remainingSlots);

      const newUploaded: UploadedFile[] = [];
      const newSizes: Record<string, number> = {};

      for (const file of filesToProcess) {
        if (maxSize && file.size > maxSize) {
          setLastError(`Ukuran file ${file.name} melebihi batas maksimum.`);
          continue;
        }

        if (!isAcceptedFile(file, accept)) {
          setLastError(`Format file ${file.name} tidak didukung.`);
          continue;
        }

        try {
          const base64 = await fileToBase64(file);
          const response = await upload({
            base64,
            fileName: file.name,
            fileType: file.type,
            storageCode: bucket,
            apiUrl,
            token,
          });

          if (response) {
            const uploadedFile = await fetchFileDetails(apiUrl, token, bucket, response.fileId);
            if (uploadedFile) {
              newUploaded.push(uploadedFile);
              newSizes[uploadedFile.id] = file.size;
            }
          }
        } catch {
          setLastError(`Gagal mengupload ${file.name}.`);
        }
      }

      if (newUploaded.length > 0) {
        const nextFiles = [...uploadedFiles, ...newUploaded];
        setUploadedFiles(nextFiles);
        setFileSizes((prev) => ({ ...prev, ...newSizes }));
        onChange?.({ uploadedFiles: nextFiles, files: [] });
      }
    },
    [accept, apiUrl, bucket, maxSize, onChange, remainingSlots, token, upload, uploadedFiles],
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(event.target.files);
    event.target.value = "";
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    handleFiles(event.dataTransfer.files);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleRemove = (index: number) => {
    const nextFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(nextFiles);
    onChange?.({ uploadedFiles: nextFiles, files: [] });
  };

  const openFileDialog = () => {
    inputRef.current?.click();
  };

  const defaultHelperText =
    maxSize || accept ? `${maxSize ? `Ukuran maksimum ${formatBytes(maxSize)} • ` : ""}Format ${buildAcceptLabel(accept)}` : undefined;

  const displayHelperText = helperText ?? defaultHelperText;
  const errorMessage = uploadError || lastError;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {canUploadMore && (
        <div
          onClick={openFileDialog}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={cn(
            "cursor-pointer rounded-xl border border-dashed p-3 transition-colors flex items-center justify-between gap-4",
            isDragging ? "border-primary bg-primary-50" : "border-gray-300 hover:border-gray-400",
            (disabled || isUploading) && "opacity-60 cursor-not-allowed",
          )}
        >
          <div className="text-sm text-gray-600">
            Klik dan letakkan file di sini atau <span className="font-semibold text-primary">Pilih File</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || isUploading}
            onClick={(event) => {
              event.stopPropagation();
              openFileDialog();
            }}
            className="font-bold text-sm"
          >
            Pilih File
          </Button>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept={accept}
            multiple={maxFiles !== 1}
            disabled={disabled || isUploading}
            onChange={handleInputChange}
          />
        </div>
      )}

      {displayHelperText && <div className="mt-2 text-xs text-gray-500">{displayHelperText}</div>}

      {/* Progress bar upload — bertambah per chunk, hilang otomatis setelah selesai */}
      {isUploading && progress !== null && (
        <div className="mt-2 flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
            <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
          <span className="min-w-9 text-right text-xs font-medium text-gray-600 tabular-nums">{progress}%</span>
        </div>
      )}

      {errorMessage && <div className="mt-2 text-xs text-red-500">{errorMessage}</div>}

      <div className="mt-3 space-y-3">
        {uploadedFiles.map((file, index) => {
          const downloadUrl = file.download_link || getDownloadUrl(apiUrl, bucket, file.id);
          const fileSize = fileSizes[file.id];

          return (
            <div key={`${file.id}-${index}`} className="flex items-center gap-3 rounded-lg border border-gray-200 p-3">
              {isImageFile(file.name) ? (
                <div className="h-12 w-12 min-w-12 rounded bg-gray-100 overflow-hidden">
                  {downloadUrl ? (
                    <img src={downloadUrl} alt={file.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <DocumentIcon className="h-6 w-6 text-gray-500" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex h-12 w-12 min-w-12 items-center justify-center rounded bg-gray-100">
                  <DocumentIcon className="h-6 w-6 text-gray-500" />
                </div>
              )}

              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-gray-900">
                  {downloadUrl ? (
                    <a
                      href={downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary hover:underline"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {file.name}
                    </a>
                  ) : (
                    file.name
                  )}
                </div>
                {fileSize !== undefined && <div className="text-xs text-gray-500">{formatBytes(fileSize)}</div>}
              </div>

              <button
                type="button"
                onClick={() => handleRemove(index)}
                disabled={disabled || isUploading}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
